import { Component, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { AppState } from '@app/core/store/app.state';
import { InitialInfo, ResetPassword } from '@app/_interfaces';
import { McToastService } from '@app/_services';
import { UserHelper } from '@app/_shared/helpers/UserHelper';
import * as CustomValidators from '@app/_validators';
import Validation from '@app/_validators/check-password.validator';
import { TranslateService } from '@ngx-translate/core';
import { Select, Store } from '@ngxs/store';
import { Observable } from 'rxjs';
import { ChangePassword, ChangeUserName } from '../store/profile.actions';
import { ProfileState } from '../store/profile.state';
import { SidebarState } from '@app/core/sidebar/store/sidebar.state';
import { environment } from '@env/environment.local';

@Component({
    selector: 'app-user-access',
    templateUrl: './user-access.component.html',
    styleUrls: ['./user-access.component.scss'],
})
export class UserAccessComponent implements OnInit {
    submitted: boolean;
    cardLoading: boolean;
    passwordForm: UntypedFormGroup;
    userNameForm: UntypedFormGroup;
    passwordError: string | null;
    loadingUserName: boolean;
    loadingPassword: boolean;
    showUserError: boolean;
    disabledUserButton: boolean;
    disabledPasswordButton: boolean;
    @Select(ProfileState.executeProfileSubmit)
    executeProfileSubmit$: Observable<boolean>;

    @Select(ProfileState.userNameError)
    userNameError$: Observable<string>;

    @Select(AppState.userName)
    userName$: Observable<string>;

    @Select(SidebarState.initialInfo)
    initialInfo$: Observable<InitialInfo>;

    userUrl: string = '';

    constructor(
        private fb: UntypedFormBuilder,
        private store: Store,
        private mcToast: McToastService,
        private translate: TranslateService,
        private userHelper: UserHelper
    ) {
        this.initialInfo$.subscribe((res) => {
            const esMarcaBlanca = res?.data?.mainMarket?.marcablanca;
            const hostName = localStorage.getItem('hostName');
            const destroyedSession = true;
            const esPWA = localStorage.getItem('esPWA') === 'true';

            // Hasta 09/10/24
            // if (environment.name !== 'PROD' && !esMarcaBlanca) {
            //     this.userUrl = `${environment.authPath}/login?destroyedSession=${destroyedSession}&host=${hostName}`;
            // }

            // if (environment.name === 'PROD' && !esMarcaBlanca) {
            //     this.userUrl = `https://micuenta.donweb.com/es-ar/servicios/sitio-simple`;
            //     return;
            // }

            // if (esMarcaBlanca) {
            //     this.userUrl = `${environment.marca_blanca.logoutPath}/login?destroyedSession=${destroyedSession}&host=${hostName}`;
            // }

            if (environment.name === 'PROD') {
                // Es Prod y Es PWA y Es MB >> https://auth.easybuild.website/login
                // Es Prod y NO Es PWA y Es MB >> https://auth.easybuild.website/login
                // Es Prod y Es PWA y Es SS >> https://auth.sitiosimple.com/login
                // Es Prod y NO Es PWA y Es SS >> https://micuenta.donweb.com/es-ar/servicios/sitio-simple

                if (esMarcaBlanca && esPWA) {
                    this.userUrl = `https://auth.easybuild.website/login`;
                }

                if (esMarcaBlanca && !esPWA) {
                    this.userUrl = `https://auth.easybuild.website/login`;
                }

                if (!esMarcaBlanca && esPWA) {
                    this.userUrl = `https://auth.sitiosimple.com/login`;
                }

                if (!esMarcaBlanca && !esPWA) {
                    this.userUrl = `https://micuenta.donweb.com/es-ar/servicios/sitio-simple`;
                }
            } else {
                // Es Incu y Es PWA y Es SS >> https://auth.incubator.newss.beer/login
                // Es Incu y Es PWA y Es MB >> http://auth.incubator.erpabruaegne.site/login
                // Es Incu y NO Es PWA y Es SS >> https://auth.incubator.newss.beer/login
                // Es Incu y NO Es PWA y Es MB >> http://auth.incubator.erpabruaegne.site/login

                // Configuracion para NO PROD
                if (!esMarcaBlanca && esPWA) {
                    this.userUrl = `${environment.authPath}/login?destroyedSession=${destroyedSession}&host=${hostName}`;
                }

                if (esMarcaBlanca && esPWA) {
                    this.userUrl = `${environment.marca_blanca.logoutPath}/login?destroyedSession=${destroyedSession}&host=${hostName}`;
                }

                if (!esMarcaBlanca && !esPWA) {
                    this.userUrl = `${environment.authPath}/login?destroyedSession=${destroyedSession}&host=${hostName}`;
                }

                if (esMarcaBlanca && !esPWA) {
                    this.userUrl = `${environment.marca_blanca.logoutPath}/login?destroyedSession=${destroyedSession}&host=${hostName}`;
                }
            }
        });
    }

    get password(): AbstractControl {
        return this.passwordForm.get('password');
    }

    get passwordConfirmation(): AbstractControl {
        return this.passwordForm.get('password_confirmation');
    }

    get userName(): AbstractControl {
        return this.userNameForm.get('userName');
    }

    ngOnInit() {
        this.hideMainButton();
        this.buildPasswordForm();
        this.buildUserNameForm();
    }

    verifyForm(): void {
        this.submitted = true;
        if (!this.password.value && !this.passwordConfirmation.value) {
            this.password.markAsDirty();
            this.password.setErrors({
                required: true,
                validators: {
                    checkMayus: true,
                    checkMinus: true,
                    checkNumber: true,
                    consecutiveNumbers: true,
                },
            });
            this.passwordConfirmation.markAsDirty();
            this.passwordConfirmation.setErrors({ required: true });
            this.passwordForm.updateValueAndValidity();
        }
    }

    buildPasswordForm(): void {
        this.passwordForm = this.fb.group(
            {
                password: ['', [Validators.required, CustomValidators.passwordFerozo(true)]],
                password_confirmation: ['', Validators.required],
            },
            { validators: Validation.match('password', 'password_confirmation') }
        );
    }

    buildUserNameForm(): void {
        this.userNameForm = this.fb.group({
            currentUsername: new UntypedFormControl({ value: '', disabled: true }),
            userName: ['', [Validators.required, Validators.maxLength(225), CustomValidators.userName()]],
        });
        this.userName$.subscribe({
            next: (userName) => {
                this.userNameForm.get('currentUsername').setValue(userName);
            },
        });
    }
    removeErrorMessage(): void {
        this.passwordError = null;
    }

    clearInputs(): void {
        this.disabledPasswordButton = true;
        this.loadingPassword = false;
        this.passwordForm.reset();
        this.password.setErrors(null);
        this.passwordConfirmation.setErrors(null);
        this.passwordForm.updateValueAndValidity();
    }

    onFormSubmit(): void {
        if (this.passwordForm.dirty) {
            this.verifyForm();
            if (this.passwordForm.valid && this.password.value.length >= 8) {
                this.loadingPassword = true;
                this.submitted = false;
                const data: ResetPassword = {
                    newpassword: this.passwordConfirmation.value,
                };
                this.store.dispatch(new ChangePassword(data)).subscribe({
                    next: () => {
                        this.clearInputs();
                    },
                    error: (err) => {
                        this.loadingPassword = false;
                        const error = err.error?.data?.message;
                        if (error === 'Contraseña incorrecta') {
                            this.passwordError = this.translate.instant('app.profile.contraseña_incorrecta');
                        } else {
                            this.mcToast.danger(
                                this.translate.instant('app.global.toast.operacion_erronea_confirmacion'),
                                'large'
                            );
                        }
                    },
                });
            }
        }
    }

    changeUserName(): void {
        const userName = this.userNameForm.get('userName').value;
        if (this.userNameForm.dirty) {
            this.loadingUserName = true;
            this.showUserError = true;
            this.store.dispatch(new ChangeUserName(userName)).subscribe({
                next: () => {
                    this.userName.setValue('');
                    this.userNameForm.setErrors(null);
                    this.loadingUserName = false;
                    this.userHelper.setUserName(userName);
                    this.userName.setErrors(null);
                    this.disabledUserButton = true;
                },
                error: () => {
                    this.loadingUserName = false;
                },
            });
        }
    }

    hideMainButton(): void {
        const button = document.getElementById('save-profile');
        if (button) {
            button.style.display = 'none';
        }
    }

    changeUserNameInput(event): void {
        this.showUserError = false;
        this.disabledUserButton = false;
    }
    changePasswordInput(event): void {
        this.disabledPasswordButton = false;
    }
}
